package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.CostSourceType
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.model.occurrencesIn
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.finance.RegenerateDailyRecordsUseCase
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

/**
 * A new month and what the weekly skeleton put into it ([skeletonError] when copying failed).
 * SH-194: [missingWeekdays] are the ISO weekdays (1 = Monday) without a day template in the week
 * as it was ([weekSkeleton]), so the app can offer to set them up and fill the month afterwards.
 */
data class CreatedRoster(
    val rosterId: String,
    val skeleton: SkeletonOutcome?,
    val skeletonError: Throwable? = null,
    val missingWeekdays: List<Int> = emptyList(),
    val weekSkeleton: Map<Int, String> = emptyMap()
) {
    companion object {
        /** The ISO weekdays [week] gives no day template. */
        fun missingWeekdays(week: Map<Int, String>): List<Int> = (1..7).filter { week[it].isNullOrBlank() }
    }
}

/**
 * Creates a month: the roster document, the cost snapshot (the owner's recurring rules copied into
 * the roster, so later rule changes never touch this month), the skeleton shifts and one daily
 * record per day. Cost and daily record problems are logged, not fatal: the roster exists and the
 * manager can retry those parts. What happened to the skeleton is returned, so an empty month is
 * explained instead of silently shown.
 *
 * Three round trips (SH-59, was 16 one after another): everything the month is made from is read
 * at once, the roster document is written (the rules of its costs and shifts look it up) while the
 * owner's cost rules are read, then the costs, the shifts and the daily records are written at the
 * same time, each in batches. The daily records are computed
 * from the costs being written instead of being read back: a new month's shifts have no cost
 * snapshots yet, so they add no labour cost.
 */
class CreateRosterUseCase(
    private val rosterRepository: RosterRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val regenerateDailyRecordsUseCase: RegenerateDailyRecordsUseCase,
    private val applyWeeklySkeletonUseCase: ApplyWeeklySkeletonUseCase,
    /** The cost snapshot reads the owner's rules and writes the month's ledger: finances only. */
    private val caller: CallerCapabilities? = null,
    /**
     * SH-172: a month created empty when availability was asked for first (no shifts yet, still
     * [RosterStatus.SKELETON]) is completed instead of refused: the weekly skeleton is copied into
     * it and its request stays linked. Null: an existing month is always refused.
     */
    private val shiftRepository: ShiftRepository? = null
) {
    class RosterAlreadyExistsException(rosterId: String) : Exception("Grafik na ten miesiąc już istnieje ($rosterId)")

    /**
     * Creates [year]-[month]'s roster at [workplaceId]; [withSkeleton] false leaves it without
     * shifts (SH-172: availability asked for before the demand is built).
     */
    suspend operator fun invoke(workplaceId: String, month: Int, year: Int, withSkeleton: Boolean = true): Result<CreatedRoster> = runCatching {
        require(month in 1..12) { "Nieprawidłowy miesiąc: $month" }
        val expectedId = RosterIds.of(workplaceId, year, month)

        coroutineScope {
            // 1. Reads, all at once. The financial ones only for those who may see money (SH-41):
            //    an operational manager's month gets its records when a financial manager opens it.
            val workplace = async { workplaceRepository.getWorkplace(workplaceId) }
            val exists = async { rosterRepository.getRoster(expectedId).isSuccess }
            val finances = async { caller.allows(workplaceId, Capability.VIEW_FINANCES) }
            val templates = async { applyWeeklySkeletonUseCase.loadTemplates(workplaceId) }
            val skeleton = async {
                workplace.await().mapCatching { wp ->
                    // No week: nothing to copy, whatever the templates' read did.
                    val used = if (wp.settings.defaultWeeklySkeleton.isEmpty()) SkeletonTemplates(emptyList(), emptyList()) else templates.await().getOrThrow()
                    // SH-55: the days the Sunday policy closes get no shifts.
                    val closed = applyWeeklySkeletonUseCase.closures(wp, year, month)
                    applyWeeklySkeletonUseCase.plan(wp, used, expectedId, year, month, closed)
                }
            }
            val costs = async {
                val ownerId = workplace.await().getOrNull()?.ownerId
                if (ownerId == null || !finances.await()) return@async emptyList()
                // The workplace's rules only: its financial managers may read those, not the owner's others.
                workplaceRepository.getRecurringCostRulesFor(ownerId, workplaceId)
                    .onFailure { println("Warning: failed to materialize costs for $expectedId: ${it.message}") }
                    .getOrDefault(emptyList())
                    .flatMap { it.occurrencesIn(year, month) }
            }
            val recordInputs = async {
                if (!finances.await()) return@async null
                regenerateDailyRecordsUseCase.loadForNewMonth(workplaceId, expectedId)
                    .onFailure { println("Warning: daily records of $expectedId not generated: ${it.message}") }
                    .getOrNull()
            }

            workplace.await().getOrThrow() // an unknown workplace creates nothing
            if (exists.await()) {
                // SH-172: the empty month of an availability request gets its skeleton now.
                if (!withSkeleton || !isEmptyMonth(expectedId)) throw RosterAlreadyExistsException(expectedId)
                val outcome = skeleton.await().mapCatching { (outcome, shifts) ->
                    if (shifts.isNotEmpty()) applyWeeklySkeletonUseCase.write(shifts).getOrThrow()
                    outcome
                }.onFailure { println("Warning: failed to apply the skeleton to $expectedId: ${it.message}") }
                val week = workplace.await().getOrThrow().settings.defaultWeeklySkeleton
                return@coroutineScope CreatedRoster(
                    expectedId, outcome.getOrNull(), outcome.exceptionOrNull(), CreatedRoster.missingWeekdays(week), week
                )
            }

            // 2. The roster document first: the rules of its costs and shifts read its workplace.
            val rosterId = rosterRepository.createRoster(workplaceId, month, year).getOrThrow()

            // 3. The month's content, written at the same time.
            val shiftsWritten = async {
                if (!withSkeleton) return@async Result.success(null)
                skeleton.await().mapCatching { (outcome, shifts) ->
                    if (shifts.isNotEmpty()) applyWeeklySkeletonUseCase.write(shifts).getOrThrow()
                    outcome
                }.onFailure { println("Warning: failed to apply the skeleton to $rosterId: ${it.message}") }
            }
            val materialized = costs.await()
            val costsWritten = async {
                if (materialized.isNotEmpty()) rosterRepository.materializeCosts(rosterId, materialized)
                    .onFailure { println("Warning: failed to materialize costs for $rosterId: ${it.message}") }
            }
            val recordsWritten = async {
                val (inputs, existing) = recordInputs.await() ?: return@async
                val fixedCosts = materialized.map {
                    FixedCost(it.rule.id, it.rule.name, it.amount, it.rule.currency, it.date, CostSourceType.GENERATED_FROM_RULE, kind = it.rule.kind, frequency = it.rule.frequency)
                }
                val records = regenerateDailyRecordsUseCase.build(workplaceId, rosterId, year, month, inputs.copy(fixedCosts = fixedCosts), existing)
                regenerateDailyRecordsUseCase.save(records)
                    .onFailure { println("Warning: daily records of $rosterId not generated: ${it.message}") }
            }
            costsWritten.await()
            recordsWritten.await()
            val outcome = shiftsWritten.await()
            val week = workplace.await().getOrThrow().settings.defaultWeeklySkeleton
            // Without the skeleton (SH-172) nothing is offered: the demand is built later.
            val missing = if (withSkeleton) CreatedRoster.missingWeekdays(week) else emptyList()
            CreatedRoster(rosterId, outcome.getOrNull(), outcome.exceptionOrNull(), missing, week)
        }
    }

    /** A month still at the demand without a single shift (created when availability was asked first). */
    private suspend fun isEmptyMonth(rosterId: String): Boolean {
        val shifts = shiftRepository ?: return false
        val roster = rosterRepository.getRoster(rosterId).getOrNull() ?: return false
        return roster.status == RosterStatus.SKELETON && shifts.getShiftsForRoster(rosterId).getOrNull()?.isEmpty() == true
    }
}
